import {
  Action, ConfirmModal, Portal, Text, useModal,
  useNotifications,
} from '@doveit/bricks';
import React from 'react';
import { updateApplicant } from '../../../providers/api/applicant/applicantProvider';
import { Applicant } from '../../../providers/api/dtos/applicant';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { JobApplication } from '../../../providers/api/dtos';
import { updateJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';
import { JobApplicationStatus } from '../../../domain/types';

export const NO_ANSWER_SUCCESS_MESSAGE = 'La chiamata senza risposta è stata registrata correttamente.';
export const NO_ANSWER_ERROR_MESSAGE = 'Non è stato possibile salvare la chiamata senza risposta.';
export const MAX_NO_ASWER_RETRY = 5;

export interface ChildFnProps {
  isLoading?: boolean,
  onClick: VoidFunction,
}

export interface NoAnswerActionProps {
  applicant: Applicant,
  jobApplication: JobApplication,
  onSuccess?: (applicant: Applicant) => void,
  children?: (props: ChildFnProps) => React.ReactNode,
}

const NoAnswerAction: React.FC<NoAnswerActionProps> = ({
  applicant,
  jobApplication,
  onSuccess,
  children,
}) => {
  const { user } = useRBAC();
  const noAnswerModal = useModal();
  const { addSuccess, addError } = useNotifications();

  const [isSaving, setIsSaving] = React.useState(false);

  const onNoAnswerClick = React.useCallback(() => noAnswerModal.open(), [noAnswerModal]);
  const onAbortModal = React.useCallback(() => noAnswerModal.close(), [noAnswerModal]);

  const noAnswerSave = React.useCallback(async () => {
    try {
      setIsSaving(true);

      const updatedApplicant = await updateApplicant(applicant.id!, {
        ...applicant,
        missedCalls: {
          counter: applicant.missedCalls.counter + 1,
          list: [{ createdBy: user.email, date: new Date().toISOString() }, ...applicant.missedCalls.list],
        },
      });

      if (updatedApplicant.missedCalls.counter >= MAX_NO_ASWER_RETRY) {
        await updateJobApplication(jobApplication.id!, {
          ...jobApplication,
          status: JobApplicationStatus.KO_UNREACHABLE,
        });
      }

      onSuccess?.(updatedApplicant);

      setIsSaving(false);
      noAnswerModal.close();
      addSuccess(NO_ANSWER_SUCCESS_MESSAGE);
    } catch (error) {
      setIsSaving(false);
      addError(NO_ANSWER_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, applicant, jobApplication, noAnswerModal, onSuccess, user.email]);

  return (
    <>
      {
        children
          ? children({ onClick: onNoAnswerClick, isLoading: isSaving })
          : (
            <Action
              label="Non risponde"
              loading={isSaving}
              onClick={onNoAnswerClick}
              aria-label="Azione per registrare che il candidato non ha risposto alla chiamata"
            />
          )
      }
      <Portal>
        <ConfirmModal
          {...noAnswerModal}
          title="Registra tentativo di chiamata"
          onConfirm={noAnswerSave}
          onAbort={onAbortModal}
          loading={isSaving}
          aria-label="Modale per registrare che il candidato non ha risposto alla chiamata"
        >
          <Text.Body>
            Il candidato non ha risposto alla chiamata. Registra il tentativo per tenerne traccia.
            Dopo 5 tentativi di chiamata senza successo, la candidatura verrà automaticamente archiviata.
          </Text.Body>
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default NoAnswerAction;
